/**
 * Billing plan catalog — single source of truth for plan limits AND marketing copy.
 *
 * Plans declare *capacity* (credits, email sends, domains, brands) and a small
 * amount of static, plan-specific copy (description, button text). The full
 * marketing feature list is GENERATED dynamically by `buildPlanFeatures`, so
 * adjusting a plan capacity updates every pricing card, settings highlight,
 * and public document automatically.
 *
 * Editing rules:
 *   - To change a plan's *capacity*, edit `BILLING_PLANS[planKey]`.
 *   - To change marketing *copy*, edit `buildPlanFeatures` (one place).
 *   - Do NOT hardcode credit-to-email or credit-to-USD numbers anywhere else.
 */

import { USD_PER_CREDIT } from './credit-economics'

export const BILLING_PLAN_ORDER = [
  'free',
  'starter',
  'growth',
  'pro',
  'enterprise',
] as const

export type BillingPlanKey = (typeof BILLING_PLAN_ORDER)[number]
export type SelfServePlanKey = Extract<
  BillingPlanKey,
  'starter' | 'growth' | 'pro'
>

export const SELF_SERVE_PLAN_KEYS = [
  'starter',
  'growth',
  'pro',
] as const satisfies ReadonlyArray<SelfServePlanKey>

export const FREE_PLAN_NAMES = new Set(['free', 'free_org'])

export const BILLING_PRICE_ENV_VARS = {
  starter: 'BILLING_STRIPE_STARTER_MONTHLY_PRICE_ID',
  growth: 'BILLING_STRIPE_GROWTH_MONTHLY_PRICE_ID',
  pro: 'BILLING_STRIPE_PRO_MONTHLY_PRICE_ID',
} as const satisfies Record<SelfServePlanKey, string>

/**
 * USD cost of generating a typical marketing email on `claude-sonnet-4.6`,
 * the default model. Used in the pricing FAQ to explain how credits are spent,
 * never to estimate how many emails a plan can produce.
 *
 * Derived from a representative request: ~20K input + ~3K output tokens at
 * Sonnet's published rates ($3/M input, $15/M output) = $0.105. Round to
 * keep the math user-friendly; the FAQ explicitly says "about".
 *
 * Update this constant if our typical email shape changes materially.
 */
export const TYPICAL_SONNET_EMAIL_COST_USD = 0.1

export type PlanFeatureBullet = Readonly<{
  id: string
  name: string
  highlighted?: boolean
}>

export type BillingPlan = Readonly<{
  key: BillingPlanKey
  name: string
  description: string
  credits: number
  emailSends: number
  /**
   * Verified sending domains. UNLIMITED on every plan
   * (`Number.MAX_SAFE_INTEGER`, the repo's unlimited sentinel) — domains are
   * not a paid differentiator; deliverability already gates them one
   * verification at a time.
   */
  domains: number
  /**
   * Maximum number of brand workspaces an org on this plan can create.
   * Use Number.MAX_SAFE_INTEGER for "unlimited".
   * Source of truth for both pricing UI and server-side enforcement
   * in `convex/brands/mutations.ts:createBrand`.
   */
  brands: number
  hasWatermark: boolean
  buttonText: string
  salesOnly?: boolean
  /**
   * SUNSET: closed to new subscriptions, but still a fully valid, ENTITLING
   * plan for orgs already on it (grandfathered).
   *
   * A sunset plan MUST stay in `BILLING_PLAN_ORDER`, in
   * `BILLING_PRICE_ENV_VARS`, in `SELF_SERVE_PLAN_KEYS`, in every Convex plan
   * validator, and in `RECONCILE_PAID_PLAN_KEYS`. Those are the *entitlement*
   * paths: `getPlanKeyFromPriceId` resolves a renewal's Stripe price through
   * the price map, and `applyInvoicePaid` REFUSES to open a paid period for a
   * price it cannot resolve — so dropping a sunset key from any of them makes
   * grandfathered orgs keep paying while silently receiving nothing, a bug
   * that only surfaces at each org's next renewal.
   *
   * The flag removes the plan from the *purchase / recommend / render* paths
   * only — see `PURCHASABLE_PLAN_KEYS`, `isPurchasablePlanKey` and
   * `getVisiblePlanKeysFor`.
   */
  isSunset?: boolean
  stripePriceEnvVar?: string
}>

/**
 * Capacity + static copy per plan. Marketing bullets are NOT stored here —
 * they're generated by `buildPlanFeatures` from the same numbers, so they
 * never drift from credit-economics.
 */
export const BILLING_PLANS = {
  free: {
    key: 'free',
    name: 'Free',
    description: 'All features. No card required.',
    credits: 500,
    emailSends: 1000,
    domains: Number.MAX_SAFE_INTEGER,
    brands: 3,
    hasWatermark: true,
    buttonText: 'Start Free',
  },
  starter: {
    key: 'starter',
    name: 'Starter',
    description: 'More volume, no watermark.',
    credits: 5000,
    emailSends: 10_000,
    domains: Number.MAX_SAFE_INTEGER,
    brands: 5,
    hasWatermark: false,
    buttonText: 'Upgrade to Starter',
    isSunset: true,
    stripePriceEnvVar: BILLING_PRICE_ENV_VARS.starter,
  },
  growth: {
    key: 'growth',
    name: 'Growth',
    description: 'More volume for growing teams.',
    credits: 10_000,
    emailSends: 50_000,
    domains: Number.MAX_SAFE_INTEGER,
    brands: 10,
    hasWatermark: false,
    buttonText: 'Upgrade to Growth',
    stripePriceEnvVar: BILLING_PRICE_ENV_VARS.growth,
  },
  pro: {
    key: 'pro',
    name: 'Pro',
    description: 'Highest self-serve volume.',
    credits: 25_000,
    emailSends: 200_000,
    domains: Number.MAX_SAFE_INTEGER,
    brands: 50,
    hasWatermark: false,
    buttonText: 'Upgrade to Pro',
    stripePriceEnvVar: BILLING_PRICE_ENV_VARS.pro,
  },
  enterprise: {
    key: 'enterprise',
    name: 'Enterprise',
    description: 'Custom volume and dedicated support.',
    credits: 0,
    emailSends: 0,
    domains: Number.MAX_SAFE_INTEGER,
    brands: Number.MAX_SAFE_INTEGER,
    hasWatermark: false,
    buttonText: 'Talk to Sales',
    salesOnly: true,
  },
} as const satisfies Record<BillingPlanKey, BillingPlan>

// ─────────────────────────────────────────────────────────────────────────
// Cost-anchored helpers
// ─────────────────────────────────────────────────────────────────────────

/** USD value of the included credits for a given plan. */
export function getPlanModelSpendUsd(planKey: BillingPlanKey): number {
  return BILLING_PLANS[planKey].credits * USD_PER_CREDIT
}

function compactCount(value: number): string {
  if (value >= 1_000_000) {
    return `${(value / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
  }
  if (value >= 1000) {
    return `${(value / 1000).toFixed(1).replace(/\.0$/, '')}K`
  }
  return value.toLocaleString('en-US')
}

function buildCreditsBullet(planKey: BillingPlanKey): PlanFeatureBullet {
  const plan = BILLING_PLANS[planKey]
  return {
    id: `${planKey}-credits`,
    name: `${compactCount(plan.credits)} AI credits / month`,
  }
}

function buildSendsBullet(planKey: BillingPlanKey): PlanFeatureBullet {
  const plan = BILLING_PLANS[planKey]
  return {
    id: `${planKey}-sends`,
    name: `${compactCount(plan.emailSends)} email sends / month`,
  }
}

function buildDomainsBullet(planKey: BillingPlanKey): PlanFeatureBullet {
  const plan = BILLING_PLANS[planKey]
  if (plan.domains >= Number.MAX_SAFE_INTEGER) {
    return { id: `${planKey}-domains`, name: 'Unlimited sending domains' }
  }
  const noun = plan.domains === 1 ? 'domain' : 'domains'
  return {
    id: `${planKey}-domains`,
    name: `${plan.domains} sending ${noun}`,
  }
}

function buildBrandsBullet(
  planKey: BillingPlanKey,
  highlighted = false
): PlanFeatureBullet {
  const plan = BILLING_PLANS[planKey]
  const noun = plan.brands === 1 ? 'brand workspace' : 'brand workspaces'
  return {
    id: `${planKey}-brands`,
    name: `${plan.brands} ${noun}`,
    ...(highlighted && { highlighted: true }),
  }
}

function buildWatermarkBullet(planKey: BillingPlanKey): PlanFeatureBullet {
  const hasWatermark = BILLING_PLANS[planKey].hasWatermark
  return {
    id: `${planKey}-watermark`,
    name: hasWatermark ? 'Brew watermark on emails' : 'No Brew watermark',
    highlighted: !hasWatermark,
  }
}

/**
 * Generate the marketing feature list for a plan. Every card is five bullets
 * so columns stay aligned and a buyer can compare one line at a time.
 *
 * Metered:     credits + sends + domains + brands + the watermark line. Paid
 *              cards repeat "No Brew watermark"
 *              instead of "Everything in <previous>" — that header implied
 *              additive capacity (500 + 10K credits) and, for Growth, that
 *              the plan inherited Free's watermark.
 * Enterprise:  five custom-volume / service lines. No metered numbers.
 */
export function buildPlanFeatures(
  planKey: BillingPlanKey
): ReadonlyArray<PlanFeatureBullet> {
  if (planKey === 'enterprise') {
    return [
      { id: 'enterprise-credits', name: 'Custom AI credit volume' },
      { id: 'enterprise-sends', name: 'Custom email send volume' },
      { id: 'enterprise-domains', name: 'Unlimited sending domains' },
      { id: 'enterprise-brands', name: 'Unlimited brand workspaces' },
      {
        id: 'enterprise-service',
        name: 'Dedicated account manager',
        highlighted: true,
      },
    ]
  }

  return [
    buildCreditsBullet(planKey),
    buildSendsBullet(planKey),
    buildDomainsBullet(planKey),
    buildBrandsBullet(planKey, planKey !== 'starter' && planKey !== 'free'),
    buildWatermarkBullet(planKey),
  ]
}

// ─────────────────────────────────────────────────────────────────────────
// Plan key utilities
// ─────────────────────────────────────────────────────────────────────────

export function isBillingPlanKey(value: string): value is BillingPlanKey {
  return value in BILLING_PLANS
}

export function isSelfServePlanKey(value: string): value is SelfServePlanKey {
  return value in BILLING_PRICE_ENV_VARS
}

export function getBillingPlan(planKey: BillingPlanKey): BillingPlan {
  return BILLING_PLANS[planKey]
}

function getBillingPlanByName(name: string): BillingPlan {
  const normalizedName = name.trim().toLowerCase()
  const planKey = BILLING_PLAN_ORDER.find((key) => key === normalizedName)
  return BILLING_PLANS[planKey ?? 'free']
}

// ─────────────────────────────────────────────────────────────────────────
// Sunset (grandfathered) plans
//
// A sunset plan is closed to new subscriptions but still fully entitling for
// the orgs already on it. The predicates below draw the line between the two
// halves of that idea — read the `sunset` doc comment on `BillingPlan` before
// touching any of them.
//
//   ENTITLEMENT / PRICE RESOLUTION  (keeps sunset plans):
//     BILLING_PLAN_ORDER · BILLING_PRICE_ENV_VARS · SELF_SERVE_PLAN_KEYS ·
//     isSelfServePlanKey · getPlanPriceIdFromEnv · getPlanKeyFromPriceId
//   PURCHASE / RECOMMEND / RENDER   (drops sunset plans):
//     PURCHASABLE_* · isPurchasablePlanKey · getVisiblePlanKeysFor
// ─────────────────────────────────────────────────────────────────────────

export function isSunsetPlanKey(planKey: BillingPlanKey): boolean {
  // Read through `getBillingPlan` so the widened `BillingPlan` type applies —
  // `BILLING_PLANS` is `as const`, so the optional `sunset` key is absent from
  // the literal type of every plan that doesn't set it.
  return getBillingPlan(planKey).isSunset === true
}

/** Tiers a NEW subscription may target. Sunset tiers are excluded. */
export const PURCHASABLE_PLAN_KEYS: ReadonlyArray<BillingPlanKey> =
  BILLING_PLAN_ORDER.filter((planKey) => !isSunsetPlanKey(planKey))

export const PURCHASABLE_SELF_SERVE_PLAN_KEYS: ReadonlyArray<SelfServePlanKey> =
  SELF_SERVE_PLAN_KEYS.filter((planKey) => !isSunsetPlanKey(planKey))

/**
 * True only for a self-serve tier that is still on sale. Use this to gate
 * checkout / plan-change targets; use {@link isSelfServePlanKey} (which keeps
 * sunset tiers) when resolving an EXISTING subscription's price.
 */
export function isPurchasablePlanKey(value: string): value is SelfServePlanKey {
  return isSelfServePlanKey(value) && !isSunsetPlanKey(value)
}

/**
 * Plans to RENDER for a given viewer. A sunset plan appears only for a viewer
 * already on it (their "Legacy" card, so they can still see and manage what
 * they pay for); nobody else ever sees it.
 */
export function getVisiblePlanKeysFor(args: {
  currentPlanKey: BillingPlanKey | null | undefined
  includeEnterprise: boolean
}): ReadonlyArray<BillingPlanKey> {
  return BILLING_PLAN_ORDER.filter((planKey) => {
    if (planKey === 'enterprise' && !args.includeEnterprise) {
      return false
    }
    if (isSunsetPlanKey(planKey)) {
      return planKey === args.currentPlanKey
    }
    return true
  })
}

export function getPlanPriceIdFromEnv(
  planKey: SelfServePlanKey,
  env: NodeJS.ProcessEnv = process.env
): string | null {
  return env[BILLING_PRICE_ENV_VARS[planKey]] ?? null
}

export function getPlanKeyFromPriceId(
  priceId: string | undefined,
  env: NodeJS.ProcessEnv = process.env
): SelfServePlanKey | null {
  if (!priceId) {
    return null
  }

  for (const planKey of SELF_SERVE_PLAN_KEYS) {
    if (env[BILLING_PRICE_ENV_VARS[planKey]] === priceId) {
      return planKey
    }
  }

  return null
}

function isFreePlanName(planName: string): boolean {
  return FREE_PLAN_NAMES.has(planName.trim().toLowerCase())
}

export function getPlanDisplayName(planName: string): string {
  return isFreePlanName(planName) ? 'Free' : getBillingPlanByName(planName).name
}

/**
 * Maximum parallel subagents — flat across plans, enforced in-memory by
 * `lib/agents/orchestrator/agent-utils.ts`. Defined here so the value is
 * import-cycle-free and the guard test can assert it without a backend.
 */
export const MAX_PARALLEL_SUBAGENTS = 5

/**
 * Per-plan parallel-subagent cap. Today flat across plans (see
 * {@link MAX_PARALLEL_SUBAGENTS}), but routed through this helper so future
 * tier-based ramping (e.g. enterprise > pro > growth > starter > free) only
 * needs to change here. `convex/billing/guards.ts:tryReserveSubagent`
 * enforces it atomically and the orchestrator agent uses it for self-throttling.
 */
export function getMaxParallelSubagents(_planKey: BillingPlanKey): number {
  return MAX_PARALLEL_SUBAGENTS
}
